// buffer-shim.ts — the MINIMAL node-Buffer surface the browser bundle needs.
// Injected by scripts/build-ts-client.mjs (esbuild `inject`), which rewrites
// every free reference to the global `Buffer` into an import of this module —
// the bundle's only Buffer consumers are io/levelpack.ts + the vendored
// sim/tools/vendor/cini.cjs (readUInt16BE / toString('latin1') /
// toString('base64')) and sim/src/level.ts (Buffer.from(b64, 'base64')).
// Only those operations are implemented; anything else throws loudly.

export class WebBuffer extends Uint8Array {
  // Overload set (type-only): the first three mirror Uint8Array.from so `WebBuffer.from`
  // remains a valid override of the base static; the last is the node-Buffer string form
  // the bundle actually calls. The single implementation signature below is unchanged.
  static override from(arrayLike: ArrayLike<number>): WebBuffer;
  static override from<T>(
    arrayLike: ArrayLike<T>,
    mapfn: (v: T, k: number) => number,
    thisArg?: unknown,
  ): WebBuffer;
  static override from(
    arrayLike: Iterable<number>,
    mapfn?: (v: number, k: number) => number,
    thisArg?: unknown,
  ): WebBuffer;
  static override from(input: string, enc?: string): WebBuffer;
  // Implementation signature: `unknown` so it subsumes every overload above (including the
  // Uint8Array mapfn forms that make `from` a valid override of the base static). The body
  // narrows `input` via `typeof`; only the string/ArrayLike paths run. Type-only.
  static override from(input: unknown, enc?: unknown): WebBuffer {
    if (typeof input === 'string') {
      if (enc === 'base64') {
        const bin = atob(input);
        const b = new WebBuffer(bin.length);
        for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
        return b;
      }
      if (enc === undefined || enc === 'latin1' || enc === 'binary' || enc === 'ascii') {
        const b = new WebBuffer(input.length);
        for (let i = 0; i < input.length; i++) b[i] = input.charCodeAt(i) & 0xff;
        return b;
      }
      throw new Error(`WebBuffer.from: unsupported encoding ${enc}`);
    }
    const src = input as ArrayLike<number>;
    const b = new WebBuffer(src.length);
    b.set(src);
    return b;
  }

  readUInt16BE(o: number): number {
    return (this[o] << 8) | this[o + 1];
  }
  readUInt32BE(o: number): number {
    return ((this[o] << 24) | (this[o + 1] << 16) | (this[o + 2] << 8) | this[o + 3]) >>> 0;
  }

  override toString(enc?: string, start = 0, end = this.length): string {
    if (enc === 'base64') {
      let bin = '';
      for (let i = start; i < end; i += 0x2000) {
        bin += String.fromCharCode(...this.subarray(i, Math.min(i + 0x2000, end)));
      }
      return btoa(bin);
    }
    if (enc === undefined || enc === 'latin1' || enc === 'binary' || enc === 'ascii') {
      let s = '';
      for (let i = start; i < end; i += 0x2000) {
        s += String.fromCharCode(...this.subarray(i, Math.min(i + 0x2000, end)));
      }
      return s;
    }
    throw new Error(`WebBuffer.toString: unsupported encoding ${enc}`);
  }
}

export { WebBuffer as Buffer };
